import React, { useState } from 'react';
import { ArrowRight, Users, TrendingUp, Award, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getCaptchaToken, verifyCaptcha } from '../utils/captcha';
import { submitForm } from '../utils/api';

const CareersPage = () => {
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    linkedinUrl: '',
    experience: '',
    cvLink: '',
    whyJoin: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // reCAPTCHA v3 verification
      const token = await getCaptchaToken('careers_form');
      const captchaResult = await verifyCaptcha(token, 'careers_form');
      if (!captchaResult.success) {
        toast.error('Verification failed. Please try again.');
        return;
      }
    } catch (err) {
      toast.error('Verification error. Please refresh and try again.');
      return;
    }

    /*
=== APPS SCRIPT — CAREERS FORM (Careers tab) ===
function onCareersSubmit(e) {
  var sheet = SpreadsheetApp.getActiveSpreadsheet()
                .getSheetByName("Careers");
  var lastRow = sheet.getLastRow();
  var name = sheet.getRange(lastRow, 2).getValue();
  var email = sheet.getRange(lastRow, 3).getValue();
  MailApp.sendEmail({
    to: email,
    subject: "Your application to Samvedanam is received",
    body: "Dear " + name + ",\n\n"
      + "Thank you for your interest in joining Samvedanam. "
      + "We have received your application and will be in "
      + "touch if a suitable opportunity arises.\n\n"
      + "Warm regards,\nTeam Samvedanam"
  });
}
=== END APPS SCRIPT ===
*/
    try {
      await submitForm('careers', applicationForm);
      toast.success('Application submitted! We\'ll review and get back to you soon.');
      setApplicationForm({
        name: '',
        email: '',
        phone: '',
        role: '',
        linkedinUrl: '',
        experience: '',
        cvLink: '',
        whyJoin: ''
      });
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('Failed to submit application. Please try again.');
    }
  };

  const benefits = [
    {
      image: '/images/stock/advisory_bg.jpg',
      title: 'High-Impact Mandates',
      description: ' Work on meaningful deals and advisory mandates that shape businesses and create lasting value'
    },
    {
      image: '/images/stock/team_collab.jpg',
      title: 'Mentorship from Industry Experts',
      description: ' Learn directly from experienced advisors with decades of consulting and investment banking experience'
    },
    {
      image: '/images/stock/experts.jpeg',
      title: 'Direct CXO Exposure',
      description: ' Collaborate directly with founders, CXOs, and business leaders on strategic initiatives'
    },
    {
      image: '/images/stock/corporate.jpg',
      title: 'Learning-First Culture',
      description: ' Flexible, outcome-oriented environment that prioritizes continuous learning and professional growth'
    }
  ];

  const openRoles = [
    {
      title: 'Investment Banking Analyst',
      location: 'Chandigarh / Delhi / Bangalore',
      type: 'Full-time',
      responsibilities: [
        'Support fundraising and M&A transactions',
        'Conduct financial modeling and valuation analysis',
        'Prepare pitch decks and information memorandums',
        'Coordinate with clients and investors'
      ],
      requirements: [
        '1-3 years experience in investment banking or consulting',
        'Strong financial modeling and Excel skills',
        'Excellent communication and presentation abilities',
        'MBA or CA preferred'
      ]
    },
    {
      title: 'Business Advisory Consultant',
      location: 'Remote / Hybrid',
      type: 'Full-time',
      responsibilities: [
        'Lead strategy and growth advisory engagements',
        'Conduct market research and competitive analysis',
        'Design business models and GTM strategies',
        'Present insights to client leadership teams'
      ],
      requirements: [
        '3-5 years in management consulting or strategy roles',
        'Strong analytical and problem-solving skills',
        'Experience with startups or MSMEs preferred',
        'MBA from top-tier institution'
      ]
    },
    {
      title: 'Investment Banking Intern',
      location: 'Chandigarh / Delhi',
      type: 'Internship',
      responsibilities: [
        'Support deal execution and research activities',
        'Assist in financial analysis and due diligence',
        'Prepare presentation materials and reports',
        'Coordinate meetings and follow-ups'
      ],
      requirements: [
        'Currently pursuing MBA, CA, or related degree',
        'Strong interest in investment banking',
        'Proficiency in MS Excel and PowerPoint',
        'Available for 3-6 months internship'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      <Header />
      {/* Hero Section */}
      <section className="pt-32 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-950/80 z-10" />
        <div className="absolute inset-0 z-0 bg-cover bg-center" style={{ backgroundImage: "url('/images/stock/team_collab.jpg')" }} />
        <div className="container mx-auto px-4 relative z-20">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-6 bg-amber-500/10 text-amber-500 border-amber-500/20">Careers at Samvedanam</Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Grow with Samvedanam
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Join a team of experienced advisors working with founders and CXOs on
              high-impact mandates. We offer a learning-first culture where you grow alongside
              meaningful work that shapes businesses.
            </p>
            <Button
              size="lg"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Apply Now for Future Openings
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Why Join Samvedanam</h2>
            <p className="text-xl text-slate-400">What makes working with us different</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              return (
                <Card key={index} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all overflow-hidden group">
                  <div className="h-32 overflow-hidden relative">
                    <img
                      src={benefit.image}
                      alt={benefit.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-slate-900/40" />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl text-white">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-400 text-sm leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>



      {/* Application Form */}
      <section id="application-form" className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-3xl text-white">Apply Now for Future Roles</CardTitle>
                <CardDescription className="text-slate-400">
                  Fill out the form below and we'll review your application
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      placeholder="Full Name *"
                      value={applicationForm.name}
                      onChange={(e) => setApplicationForm({ ...applicationForm, name: e.target.value })}
                      required
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                    <Input
                      type="email"
                      placeholder="Email *"
                      value={applicationForm.email}
                      onChange={(e) => setApplicationForm({ ...applicationForm, email: e.target.value })}
                      required
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      type="tel"
                      placeholder="Phone"
                      value={applicationForm.phone}
                      onChange={(e) => setApplicationForm({ ...applicationForm, phone: e.target.value })}
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                    <Input
                      placeholder="Role Applying For *"
                      value={applicationForm.role}
                      onChange={(e) => setApplicationForm({ ...applicationForm, role: e.target.value })}
                      required
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      placeholder="LinkedIn URL"
                      value={applicationForm.linkedinUrl}
                      onChange={(e) => setApplicationForm({ ...applicationForm, linkedinUrl: e.target.value })}
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                    <Input
                      placeholder="Years of Experience"
                      value={applicationForm.experience}
                      onChange={(e) => setApplicationForm({ ...applicationForm, experience: e.target.value })}
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <Input
                    placeholder="Link to CV / Resume *"
                    value={applicationForm.cvLink}
                    onChange={(e) => setApplicationForm({ ...applicationForm, cvLink: e.target.value })}
                    required
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                  <Textarea
                    placeholder="Why do you want to work with Samvedanam? *"
                    value={applicationForm.whyJoin}
                    onChange={(e) => setApplicationForm({ ...applicationForm, whyJoin: e.target.value })}
                    required
                    rows={4}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                  <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                    Apply Now for Future Roles
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    By submitting, you agree to our processing of your application data
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CareersPage;
